/**
 * `?league=<id>` on the NFL shell: make an inbound link's league the active one.
 *
 * Email CTAs are COLD links. Unlike a tapped push -- where handleNotificationTap can call
 * setActiveLeague() before navigating, because it is already running in the app -- an email
 * arrives with nothing but a URL. The league has to travel in the URL and be applied on
 * arrival, or a multi-league user who clicks a League B email lands in whichever league was
 * active last, reading League A's data under League B's headline.
 *
 * ── ORDER IS THE WHOLE POINT ───────────────────────────────────────────────
 *
 * The param must be applied BEFORE NFLLeagueProvider resolves its league, and therefore
 * before the draft tab judges rule (e) from draft_status. Apply it after, and the room is
 * chosen from the OLD league's status -- so a push or mail about a pending league can drop
 * someone into the new room while their other league is mid-draft, which is exactly the
 * failure rule (e) exists to prevent. Same ordering hazard as the push path, arrived at
 * from a different direction.
 *
 * The mechanism is deliberately the same one SAB-577 already uses: write the id to
 * sf_active_nfl_league_id, and let the provider's existing read/validate half pick it up.
 * A second, parallel way of selecting a league would be a second source of truth.
 *
 * ── THIS IS NOT AN AUTHORISATION BOUNDARY ──────────────────────────────────
 *
 * Anyone can put any id in a URL. That is safe here because resolveActiveNflLeagueId
 * honours a cached id ONLY if it is a league the user is currently a member of, and falls
 * back to their first league otherwise. So a forged link cannot show anyone a league they
 * are not in -- it can at most select, from among their OWN leagues, one they did not
 * expect. Validation below is therefore about shape, not permission; the membership check
 * stays where it already is.
 */

/** The query-string key. One place, so a link builder and this reader cannot drift. */
export const LEAGUE_PARAM = 'league';

/**
 * The league id an inbound URL is asking for, or null.
 *
 * Accepts a `URLSearchParams`, a raw query string, or null. Trims, and treats blank as
 * absent -- `?league=` is a link-builder bug, not a request to select a league named "".
 */
export function leagueIdFromSearch(search: URLSearchParams | string | null | undefined): string | null {
  if (search == null) return null;
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const raw = params.get(LEAGUE_PARAM);
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Append `?league=<id>` to a route. Used by link builders so the key is never retyped. */
export function withLeagueParam(route: string, leagueId: string | null | undefined): string {
  const id = typeof leagueId === 'string' ? leagueId.trim() : '';
  if (!id) return route;
  const sep = route.includes('?') ? '&' : '?';
  return `${route}${sep}${LEAGUE_PARAM}=${encodeURIComponent(id)}`;
}
